import { TherapistInfoDto } from '../../../../shared/dtos/therapist.dto';
import React, { FC, ReactElement, useMemo, useState } from 'react';
import { useAuthFetchFromRestAPI } from '../../util/api';
import * as Yup from 'yup';
import i18next from 'i18next';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { ChangeTherapistRequest } from '../../../../shared/requests/changeTherapist.request';
import { Alert, Button, Form, Row, Spinner, Stack } from 'react-bootstrap';
import Select, { SingleValue } from 'react-select';
import Image from 'react-bootstrap/esm/Image';
import { ChangeTherapist } from './PatientsTable';

type FormValues = {
  therapistId: string;
}

type ChangeTherapistFormProps = {
  data: ChangeTherapist,
  therapistInfos: TherapistInfoDto[]
  onClose: () => void;
  onSubmit: () => void;
}

type TherapistOption = {
  value: string;
  label: string;
  profilePic?: string;
}

const ChangeTherapistForm: FC<ChangeTherapistFormProps> = ({
  data,
  therapistInfos,
  onClose,
  onSubmit
}: ChangeTherapistFormProps): ReactElement => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { authFetch } = useAuthFetchFromRestAPI();

  const initialValues: FormValues = { therapistId: '' };

  const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object().shape({
    therapistId: Yup.string().required(i18next.t('generic.required')),
  });
  
  const therapistOptions: TherapistOption[] = useMemo(() => {
    return therapistInfos.filter((info) => info.id !== data.currentTherapistId).map((info) => ({
      value: info.id,
      label: `${info.firstName} ${info.lastName}`,
      profilePic: info.profilePic,
    }));
  }, [therapistInfos]);
  
  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    setErrorMessage(null);
    setIsSaving(true);
    setSubmitting(true);
      
    try {
      const request: ChangeTherapistRequest = { patientId: data.patientId, newTherapistId: values.therapistId, oldTherapistId: data.currentTherapistId };
      await authFetch('/api/v1/admin/change-therapist', { method: 'POST', body: request });
      onSubmit();
    } catch (error: any) {
      console.error(error);
      const msg: string = error?.message ?? (typeof error === 'string' ? error : i18next.t('generic.unknownError'));
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };
    
  return (
    <div className="position-relative">
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
      )}
        
      <Formik 
        enableReinitialize
        initialValues={initialValues} 
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ values, setFieldValue, touched, errors, handleSubmit, handleBlur, isSubmitting }: FormikProps<FormValues>) => {
          const selectedOption = therapistOptions.find((option) => option.value === values.therapistId) ?? null;
          
          return (
            <Form
              noValidate
              onSubmit={handleSubmit}
              className="p-2">
              <Stack gap={3}>
                <Row>
                  <Form.Group controlId="therapistId">
                    <Form.Label>
                      {i18next.t('changeTherapistForm.therapistId')}
                    </Form.Label>

                    <Select<TherapistOption, false>
                      name="therapistId"
                      value={selectedOption}
                      options={therapistOptions}
                      isDisabled={isSaving || isSubmitting}
                      onChange={(option: SingleValue<TherapistOption>) => setFieldValue('therapistId', option?.value ?? '')}
                      onBlur={() => handleBlur({ target: { name: 'therapistId' } })}
                      classNamePrefix="react-select"
                      placeholder={i18next.t('changeTherapistForm.therapistIdPlaceholder')}
                      formatOptionLabel={(option) => (
                        <div className="d-flex align-items-center gap-2">
                          <Image
                            src={option.profilePic ? option.profilePic : '../default_profile.png'}
                            roundedCircle={true}
                            className='border'
                            width='24px'
                            height='24px'
                          />
                          <span>{option.label}</span>
                        </div>
                      )}
                    />

                    {touched.therapistId && errors.therapistId && (
                      <div className="invalid-feedback d-block">
                        {errors.therapistId}
                      </div>
                    )}
                  </Form.Group>
                </Row>
                  
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting || isSaving}
                  >
                    {i18next.t('generic.close')}
                  </Button>

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || isSaving}
                  >
                    {i18next.t('changeTherapistForm.save')}
                  </Button>
                </div>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ChangeTherapistForm;
